import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, map, Observable, Subscription } from 'rxjs';
import { AutorizationModalComponent } from '../../../components/common/autorization-modal/autorization-modal.component';
import { getFromStorage, saveToStorage } from '../../../helpers/storageUtils';
import { Country } from '../../../Models/paises';
import { OnerousStateService } from '../../../shared/services/onerous/onerous-state.service';
import { FormUtils } from '../../../shared/utils/forms-utils';
import { RemainingDataFormInterface } from '../../interfaces/remainingDataForm.interface';
import {
  invalidFirstLastNameString,
  invalidFirstNameString,
  invalidSecondLastNameString,
  invalidSecondNameString,
} from '../../utils/invalid-strings.utils';
import {
  emailValidator,
  exactLengthValidator,
  numericValidator,
  restrictedEmailsValidator,
  startsWithValidatorNumber,
} from '../../utils/motos-forms-utils';
import {
  allowedCharactersValidator,
  alphabeticValidator,
  compositeLastNameValidator,
  documentNumberValidator,
  lengthValidator,
  maxCurrentDateValidator,
  minYearsFromBirthValidator,
  payerFields,
  RemainingDataFormUtils,
  restrictedDatesValidator,
  restrictedPhrasesValidator,
  restrictedStringsValidator,
  togglePayerFieldsValidation,
} from '../../utils/remaining-data-form.utils';


@Component({
  selector: 'motos-remaining-data-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule],
  templateUrl: './remaining-data-form.component.html',
  styleUrl: './remaining-data-form.component.scss',
})
export class RemainingDataFormComponent implements OnInit, OnChanges, OnDestroy {
  private fb = inject(FormBuilder);
  private onerousStateService = inject(OnerousStateService);
  modalService = inject(NgbModal);

  @Output() formSubmit = new EventEmitter<RemainingDataFormInterface>();
  @Input() cities: string[] = [];
  @Input() countries: string[] = [];
  @Input() countriesFullData: Country[] = [];
  @Input() numberSelected: string | undefined;
  @Input() dateFromRunt: string | undefined;

  payerFieldsOriginalValidators: Record<string, ValidatorFn | ValidatorFn[]> = {};
  selectedOnerous: any = null;
  formattedAddress = '';
  formattedPayerAddress = '';

  formFromStorage: RemainingDataFormInterface | null = getFromStorage<RemainingDataFormInterface>(
    'remainingDataForm',
    'session',
  );

  attempsFromStorage: number = getFromStorage<number>('sarlaftAttemps', 'session') || 0;

  private readonly formStateStorageKey = 'remainingDataFormState';
  private formChangesSubscription?: Subscription;
  // Expresión regular para los caracteres permitidos
  private allowedAddressRegex = /^[A-Za-z0-9Ññ\s\.\-#\*\/\\]+$/;
  documentTypes = [
    { label: 'C.C', value: 'C' },
    { label: 'C.E.', value: 'E' },
    { label: 'P.', value: 'P' },
  ];

  formUtils = FormUtils;
  remainingDataFormUtils = RemainingDataFormUtils;
  remainingDataForm!: FormGroup;

  // Estados de validación para reactividad - Objeto optimizado
  fieldStates: Record<string, { valid: boolean; invalid: boolean; touched: boolean }> = {
    // Campos principales
    bornDate: { valid: false, invalid: false, touched: false },
    expeditionDate: { valid: false, invalid: false, touched: false },
    country: { valid: false, invalid: false, touched: false },
    city: { valid: false, invalid: false, touched: false },
    address: { valid: false, invalid: false, touched: false },
    number: { valid: false, invalid: false, touched: false },
    email: { valid: false, invalid: false, touched: false },
    // Campos del pagador
    payerFirstName: { valid: false, invalid: false, touched: false },
    payerSecondName: { valid: false, invalid: false, touched: false },
    payerFirstLastName: { valid: false, invalid: false, touched: false },
    payerSecondLastName: { valid: false, invalid: false, touched: false },
    payerDocument: { valid: false, invalid: false, touched: false },
    payerExpeditionDate: { valid: false, invalid: false, touched: false },
    payerBornDate: { valid: false, invalid: false, touched: false },
    payerNumber: { valid: false, invalid: false, touched: false },
    payerEmail: { valid: false, invalid: false, touched: false },
    payerCountry: { valid: false, invalid: false, touched: false },
    payerCity: { valid: false, invalid: false, touched: false },
    payerAddress: { valid: false, invalid: false, touched: false },
  };

  // Métodos genéricos para obtener estados de validación
  isFieldValid(fieldName: string): boolean {
    return this.fieldStates[fieldName]?.valid || false;
  }

  isFieldInvalid(fieldName: string): boolean {
    return this.fieldStates[fieldName]?.invalid || false;
  }

  isFieldTouched(fieldName: string): boolean {
    return this.fieldStates[fieldName]?.touched || false;
  }

  ngOnInit(): void {
    // 1. Obtener la data guardada previamente (soporta recarga o regreso)
    const savedStateRaw = sessionStorage.getItem(this.formStateStorageKey);
    const savedState = savedStateRaw ? JSON.parse(savedStateRaw) : null;

    // Prioridad de datos: 1. Estado en sesión (si regresó) | 2. Propiedad del storage | 3. Valor por defecto
    const initialValues = savedState || this.formFromStorage || {};
    this.selectedOnerous = this.onerousStateService.selection();

    this.remainingDataForm = this.fb.group({
      bornDate: [initialValues.bornDate || this.dateFromRunt || '', [Validators.required]],
      expeditionDate: [
        initialValues.expeditionDate || '',
        [
          Validators.required,
          restrictedDatesValidator(),
          maxCurrentDateValidator(),
          minYearsFromBirthValidator('bornDate'),
        ],
      ],
      country: [
        initialValues.country || 'Colombia',
        [Validators.required, this.validateInList(this.countries)],
      ],
      city: [
        initialValues.city || '',
        [Validators.required, this.validateInList(this.cities)],
      ],
      address: [
        initialValues.address || '',
        [Validators.required, allowedCharactersValidator(), restrictedPhrasesValidator()],
      ],
      number: [
        initialValues.number || this.numberSelected || '',
        [
          Validators.required,
          numericValidator(),
          exactLengthValidator(10),
          startsWithValidatorNumber(300, 350),
        ],
      ],
      email: [
        initialValues.email || '',
        [Validators.required, emailValidator(), restrictedEmailsValidator()],
      ],
      termsAndConditions: [
        initialValues.termsAndConditions || false,
        [Validators.requiredTrue],
      ],
      isPayer: new FormControl(initialValues.isPayer || 'Y', Validators.required),
      payerFirstName: [
        initialValues.payerFirstName || '',
        [
          alphabeticValidator(),
          lengthValidator(2, 100),
          restrictedStringsValidator(invalidFirstNameString),
        ],
      ],
      payerSecondName: [
        initialValues.payerSecondName || '',
        [
          alphabeticValidator(),
          lengthValidator(0, 30),
          restrictedStringsValidator(invalidSecondNameString),
        ],
      ],
      payerFirstLastName: [
        initialValues.payerFirstLastName || '',
        [
          lengthValidator(3, 30),
          restrictedStringsValidator(invalidFirstLastNameString),
          compositeLastNameValidator(),
        ],
      ],
      payerSecondLastName: [
        initialValues.payerSecondLastName || '',
        [
          alphabeticValidator(),
          lengthValidator(0, 30),
          restrictedStringsValidator(invalidSecondLastNameString),
        ],
      ],
      payerIdentificationType: [initialValues.payerIdentificationType || '', []],
      payerDocument: [
        initialValues.payerDocument || '',
        [documentNumberValidator('payerIdentificationType')],
      ],
      payerExpeditionDate: [
        initialValues.payerExpeditionDate || '',
        [
          restrictedDatesValidator(),
          maxCurrentDateValidator(),
          minYearsFromBirthValidator('payerBornDate'),
        ],
      ],
      payerBornDate: [
        initialValues.payerBornDate || '',
        [restrictedDatesValidator(), maxCurrentDateValidator()],
      ],
      payerCountry: [
        initialValues.payerCountry || 'Colombia',
        [this.validateInList(this.countries)],
      ],
      payerCity: [initialValues.payerCity || '', [this.validateInList(this.cities)]],
      payerAddress: [
        initialValues.payerAddress || '',
        [allowedCharactersValidator(), restrictedPhrasesValidator()],
      ],
      payerNumber: [
        initialValues.payerNumber || '',
        [
          numericValidator(),
          exactLengthValidator(10),
          startsWithValidatorNumber(300, 350),
        ],
      ],
      payerEmail: [
        initialValues.payerEmail || '',
        [emailValidator(), restrictedEmailsValidator()],
      ],
    });

    if (this.countries?.length > 0 || this.cities?.length > 0) {
      this.updateListValidators();
    }

    payerFields.forEach((field) => {
      const control = this.remainingDataForm.get(field);
      if (control && control.validator) {
        this.payerFieldsOriginalValidators[field] = control.validator;
      }
    });

    this.remainingDataForm.get('isPayer')?.valueChanges.subscribe((value) => {
      togglePayerFieldsValidation(
        this.remainingDataForm,
        value === 'N',
        this.payerFieldsOriginalValidators,
      );
    });

    this.remainingDataForm.get('payerBornDate')?.valueChanges.subscribe(() => {
      this.remainingDataForm.get('payerExpeditionDate')?.updateValueAndValidity();
    });

    this.remainingDataForm.get('payerIdentificationType')?.valueChanges.subscribe(() => {
      this.remainingDataForm.get('payerDocument')?.updateValueAndValidity();
    });

    // Re-validar campos si venían cargados desde memoria
    if (initialValues) {
      this.validatePrefilledFields();
    }

    this.listenFormChanges();
  }

  ngOnDestroy(): void {
    this.formChangesSubscription?.unsubscribe();
  }

  // 1. Cambiar la lectura inicial a sessionStorage o validar la sesión
  private loadFormState(): void {
    const isDiligenced = sessionStorage.getItem('isFormDiligenced');

    // Si la sesión no ha sido iniciada en este navegador/pestaña, borrar el borrador viejo
    if (!isDiligenced) {
      localStorage.removeItem(this.formStateStorageKey);
      sessionStorage.removeItem('remainingDataForm');
      return;
    }

    const raw = sessionStorage.getItem(this.formStateStorageKey); // 👈 Cambiado a sessionStorage
    if (!raw) {
      return;
    }

    try {
      const savedState = JSON.parse(raw);
      if (savedState && typeof savedState === 'object') {
        this.remainingDataForm.patchValue(savedState, { emitEvent: false });
        this.validatePrefilledFields();
      }
    } catch (error) {
      console.error('No se pudo cargar el estado guardado del formulario', error);
    }
  }

  // 2. Guardar también en sessionStorage para que expire al cerrar el navegador
  private saveFormState(): void {
    sessionStorage.setItem(this.formStateStorageKey, JSON.stringify(this.remainingDataForm.getRawValue()));
  }

  private listenFormChanges(): void {
    this.formChangesSubscription = this.remainingDataForm.valueChanges
      .pipe(debounceTime(200))
      .subscribe(() => this.saveFormState());
  }

  /**
   * Valida y marca como touched todos los campos que tienen datos pre-llenados
   * desde sessionStorage (retoma de sesión).
   */
  private validatePrefilledFields(): void {
    const fieldsToValidate = [
      'bornDate',
      'expeditionDate',
      'country',
      'city',
      'address',
      'number',
      'email',
    ];

    // Validar campos principales
    fieldsToValidate.forEach((fieldName) => {
      const control = this.remainingDataForm.get(fieldName);
      if (control && control.value !== null && control.value !== '') {
        if (fieldName === 'country' || fieldName === 'city') {
          const list = fieldName === 'country' ? this.countries : this.cities;
          if (!list || list.length === 0) {
            if (this.fieldStates[fieldName]) {
              this.fieldStates[fieldName].valid = true;
              this.fieldStates[fieldName].invalid = false;
            }
            return;
          }
        }
        control.markAsTouched();
        control.markAsDirty();
        this.setFieldTouched(fieldName);
        this.validateField(fieldName);
      }
    });

    // Si el pagador es diferente del dueño, validar campos del pagador
    if (this.remainingDataForm.get('isPayer')?.value === 'N') {
      const payerFieldsToValidate = [
        'payerFirstName',
        'payerSecondName',
        'payerFirstLastName',
        'payerSecondLastName',
        'payerIdentificationType',
        'payerDocument',
        'payerExpeditionDate',
        'payerBornDate',
        'payerCountry',
        'payerCity',
        'payerAddress',
        'payerNumber',
        'payerEmail',
      ];

      payerFieldsToValidate.forEach((fieldName) => {
        const control = this.remainingDataForm.get(fieldName);
        if (control && control.value !== null && control.value !== '') {
          if (fieldName === 'payerCountry' || fieldName === 'payerCity') {
            const list = fieldName === 'payerCountry' ? this.countries : this.cities;
            if (!list || list.length === 0) {
              if (this.fieldStates[fieldName]) {
                this.fieldStates[fieldName].valid = true;
                this.fieldStates[fieldName].invalid = false;
              }
              return;
            }
          }
        }
      });
    }

    // Forzar actualización de validez del formulario
    this.remainingDataForm.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const countriesChanged = changes['countries'] && changes['countries'].currentValue?.length > 0;
    const citiesChanged = changes['cities'] && changes['cities'].currentValue?.length > 0;
    const countriesFullDataChanged =
      changes['countriesFullData'] && changes['countriesFullData'].currentValue?.length > 0;

    // Si se recibió la lista completa de países, mapear códigos a etiquetas
    if (this.remainingDataForm && countriesFullDataChanged) {
      this.mapCountryCodesToLabels();
    }

    // Si el formulario ya existe Y alguna de las listas ha cambiado y ahora tiene datos...
    if (this.remainingDataForm && (countriesChanged || citiesChanged)) {
      this.payerFieldsOriginalValidators['payerCountry'] = [this.validateInList(this.countries)];
      this.payerFieldsOriginalValidators['payerCity'] = [this.validateInList(this.cities)];

      this.updateListValidators();

      // Re-validar campos pre-llenados ahora que las listas están disponibles

      // Pequeño delay para asegurar que los validadores se actualicen primero
      setTimeout(() => this.validatePrefilledFields(), 0);

    }
  }

  /**
   * Mapea códigos de país a etiquetas usando la lista completa de países.
   * Esto es necesario para la retoma donde el backend devuelve códigos como "CO", "SK", etc.
   */
  private mapCountryCodesToLabels(): void {
    const countryControl = this.remainingDataForm.get('country');
    const payerCountryControl = this.remainingDataForm.get('payerCountry');

    // Mapear country del owner
    if (countryControl?.value) {
      const mappedLabel = this.getCountryLabelByCode(countryControl.value);
      if (mappedLabel && mappedLabel !== countryControl.value) {
        countryControl.setValue(mappedLabel, { emitEvent: false });
      }
    }

    // Mapear country del payer
    if (payerCountryControl?.value) {
      const mappedLabel = this.getCountryLabelByCode(payerCountryControl.value);
      if (mappedLabel && mappedLabel !== payerCountryControl.value) {
        payerCountryControl.setValue(mappedLabel, { emitEvent: false });
      }
    }
  }

  /**
   * Obtiene la etiqueta de un país dado su código.
   * Si el valor ya es una etiqueta válida, lo retorna sin cambios.
   */
  private getCountryLabelByCode(codeOrLabel: string): string {
    if (!codeOrLabel) return '';

    // Si ya está en la lista de etiquetas, retornar sin cambios
    if (this.countries.includes(codeOrLabel)) {
      return codeOrLabel;
    }

    // Buscar por código en la lista completa
    const country = this.countriesFullData.find(
      (c) => c.value?.toUpperCase() === codeOrLabel.toUpperCase(),
    );

    return country?.label || codeOrLabel;
  }

  validateInList(allowed: string[]) {
    return (control: AbstractControl) => {
      if (!control.hasValidator(Validators.required)) {
        return null;
      }
      if (!control.value) {
        return null; // El valor está vacío, permite que lo valide 'Validators.required'
      }
      return allowed.includes(control.value) ? null : { notInList: true };
    };
  }

  // Función para buscar países
  searchCountry = (text$: Observable<string>): Observable<string[]> => {
    return text$.pipe(
      debounceTime(300),
      map((term) =>
        term.length < 3
          ? []
          : this.countries.filter((v) => v.toLowerCase().includes(term.toLowerCase())),
      ),
    );
  };

  // Función para buscar ciudades
  searchCity = (text$: Observable<string>): Observable<string[]> => {
    return text$.pipe(
      debounceTime(300),
      map((term) =>
        term.length < 3
          ? []
          : this.cities.filter((v) => v.toLowerCase().includes(term.toLowerCase())),
      ),
    );
  };

  // Formatear los resultados de autocomplete para mostrarlos y seleccionarlos
  formatter = (result: string): string => result;

  validateSelection(fieldName: string): void {
    const control = this.remainingDataForm.get(fieldName);
    const value = control?.value;
    const isValid =
      fieldName === 'country' ? this.countries.includes(value) : this.cities.includes(value);
    if (!isValid) {
      control?.setErrors({ notInList: true });
      control?.markAsTouched();
    } else {
      control?.setErrors(null);
    }
  }

  /**
   * Captura el evento de entrada en el input y filtra los caracteres no permitidos,
   * además de convertir cualquier texto ingresado a mayúsculas.
   * @param event Input event from the HTML input field
   */
  onAddressInput(event: Event, isPayer = false): void {
    const inputElement = event.target as HTMLInputElement;

    // Filtra los caracteres permitidos y convierte el texto a mayúsculas
    const newValue = inputElement.value
      .toUpperCase()
      .split('')
      .filter((char) => this.allowedAddressRegex.test(char))
      .join('');

    isPayer ? (this.formattedPayerAddress = newValue) : (this.formattedAddress = newValue);
    const valueField = isPayer ? 'payerAddress' : 'address';
    // Actualizamos el control del formulario con el valor formateado
    this.remainingDataForm.get(valueField)?.setValue(newValue);
  }

  // Función para limpiar y convertir el texto a minúsculas en tiempo real
  formatEmailField(isPayer = false): void {
    const valueField = isPayer ? 'payerEmail' : 'email';
    const emailControl = this.remainingDataForm.get(valueField);
    if (emailControl?.value) {
      const formattedEmail = emailControl.value.trim().toLowerCase(); // Limpiar espacios y convertir a minúsculas
      emailControl.setValue(formattedEmail, { emitEvent: false }); // Actualizar valor sin generar eventos infinitos
    }
  }

  onSubmit(): void {
    saveToStorage('remainingDataForm', this.remainingDataForm.value, 'session');
    this.formSubmit.emit(this.remainingDataForm.value);
  }

  // Métodos de validación con reactividad - Optimizado
  validateField(fieldName: string): void {
    const control = this.remainingDataForm.get(fieldName);
    const isValid = control?.valid && control?.value;

    // Verificar si el campo existe en fieldStates
    if (this.fieldStates[fieldName]) {
      const fieldState = this.fieldStates[fieldName];
      fieldState.valid = isValid && fieldState.touched;
      fieldState.invalid = !isValid && fieldState.touched;
    }
  }

  // Método helper para obtener el estado de un campo
  getFieldState(fieldName: string, state: 'valid' | 'invalid' | 'touched'): boolean {
    return this.fieldStates[fieldName]?.[state] || false;
  }

  // Método helper para establecer el estado touched de un campo
  setFieldTouched(fieldName: string): void {
    if (this.fieldStates[fieldName]) {
      this.fieldStates[fieldName].touched = true;
    }
  }

  onFieldBlur(fieldName: string): void {
    this.setFieldTouched(fieldName);
    this.validateField(fieldName);
  }

  openModal() {
    const modalRef = this.modalService.open(AutorizationModalComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.title = 'Política de privacidad y Tratamiento de datos personales';

    modalRef.componentInstance.close.subscribe(() => {
      modalRef.close();
    });
  }

  /**
   * Sanitiza el valor del input en tiempo real para permitir solo dígitos.
   * Este método se asegura de que el modelo del formulario se actualice correctamente,
   * solucionando el problema con el validador 'required' al vaciar el campo.
   * @param event El evento del input.
   */
  onNumberInput(event: Event, isPayer: boolean): void {
    const fieldName = isPayer ? 'payerNumber' : 'number';
    const input = event.target as HTMLInputElement;
    const originalValue = input.value;
    const sanitizedValue = originalValue.replace(/\D/g, ''); // Elimina todo lo que no sea dígito

    const control = this.remainingDataForm.get(fieldName);

    // Actualiza el valor en el control del formulario SIN emitir un evento
    // para no causar un bucle infinito.
    control?.setValue(sanitizedValue, { emitEvent: false });

    // Si la sanitización cambió el valor, actualiza también el valor del
    // elemento input en el DOM directamente. Esto asegura que la vista y el modelo
    // estén sincronizados. Actualizar .value programáticamente NO dispara otro evento 'input'.
    if (originalValue !== sanitizedValue) {
      input.value = sanitizedValue;
    }

    // Marca el campo como 'touched' para asegurar que los mensajes de error
    // (como 'required') se muestren inmediatamente si el campo se vacía.
    control?.markAsTouched();
  }

  /**
   * Re-establece los validadores para los campos que dependen de listas asíncronas
   * y fuerza una re-validación de su estado actual.
   */
  updateListValidators(): void {
    const countryControl = this.remainingDataForm.get('country');
    const payerCountryControl = this.remainingDataForm.get('payerCountry');
    const cityControl = this.remainingDataForm.get('city');
    const payerCityControl = this.remainingDataForm.get('payerCity');
    const isPayer = this.remainingDataForm.get('isPayer')?.value;

    if (countryControl) {
      countryControl.setValidators([Validators.required, this.validateInList(this.countries)]);
      // Actualiza el estado de este control específico.
      countryControl.updateValueAndValidity();
    }

    if (cityControl) {
      // Repite el proceso para la ciudad.
      cityControl.setValidators([Validators.required, this.validateInList(this.cities)]);
      cityControl.updateValueAndValidity();
    }

    if (payerCountryControl && isPayer === 'N') {
      payerCountryControl.setValidators([Validators.required, this.validateInList(this.countries)]);
      payerCountryControl.updateValueAndValidity();
      console.log('payerCountryControl', payerCountryControl);
    }

    if (payerCityControl && isPayer === 'N') {
      payerCityControl.setValidators([Validators.required, this.validateInList(this.cities)]);
      payerCityControl.updateValueAndValidity();
    }
  }

  blockUnicode(event: KeyboardEvent) {
    // Bloquea ñ y Ñ
    if (event.key === 'ñ' || event.key === 'Ñ') {
      event.preventDefault();
    }
  }
}
